/* Phase 2 primitive: the one HMS modal shell.
 *
 * A portal to document.body, so the panel can never be clipped by an
 * ancestor's overflow or out-stacked by a screen's own z-index ladder —
 * the trap every hand-rolled modal in the app has had to solve on its
 * own.
 *
 * Props:
 *  - isOpen:         renders nothing when false
 *  - onClose:        called by ESC, by the overlay click, by the X, and
 *                    by whatever the footer wires up
 *  - title:          the accessible name; rendered as the panel's h2
 *  - header:         optional custom header, pinned above the scrolling
 *                    body (see "Custom headers" below)
 *  - children:       panel body — the ONLY part that scrolls
 *  - footer:         right-aligned action slot above the panel's foot
 *  - width:          number (px) or any CSS length; defaults to the
 *                    stylesheet's 600px
 *  - showClose:      top-right X in the header row (default false)
 *  - busy:           mid-save lock (default false) — every close path
 *                    (ESC, overlay, X) is refused while it is true
 *  - padded:         false strips the panel's own padding so header,
 *                    body and footer can run edge to edge (default true)
 *  - closeOnOverlay: click the backdrop to close (default true)
 *  - className:      extra hook on the panel
 *
 * Custom headers
 * --------------
 * `header` takes either a node or a render function. The accessible name
 * must stay the title ALONE — an aria-labelledby pointing at a header
 * row would drag the eyebrow text, the status chips and the X's label
 * into the dialog's name. So the h2 is built here and handed to the
 * caller to position:
 *
 *   header={(titleNode) => (
 *     <div className="my-bar">
 *       <div className="my-eyebrow">Block editor</div>
 *       <div className="my-row">{titleNode}<Chip /></div>
 *     </div>
 *   )}
 *
 * Take the node form instead and the h2 is rendered above whatever you
 * pass. Either way `title` stays required for a named dialog: with the
 * render form you MUST place `titleNode`, or aria-labelledby will point
 * at an id that is not in the DOM.
 *
 * Dialog behaviour owned here, once: ESC closes, focus moves into the
 * panel on open and back to the trigger on close, Tab cycles inside the
 * panel, and the body cannot scroll behind the overlay.
 */
import React, { useEffect, useId, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './HMSModal.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
  ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const HMSModal = ({
  isOpen,
  onClose,
  title,
  header,
  children,
  footer,
  width,
  showClose = false,
  busy = false,
  padded = true,
  closeOnOverlay = true,
  className = '',
}) => {
  const panelRef = useRef(null);
  const titleId = useId();
  // The handlers below live for as long as the modal is open, but they
  // must see the LATEST onClose and busy. Keeping both in refs lets the
  // open effect depend on `isOpen` alone — depend on the values and
  // every parent re-render would tear the effect down and yank focus
  // back to the trigger mid-session.
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  useEffect(() => {
    onCloseRef.current = onClose;
    busyRef.current = busy;
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    const focusable = () =>
      Array.from(panelRef.current ? panelRef.current.querySelectorAll(FOCUSABLE) : []);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Swallowed either way: a modal owns ESC, so the key must not
        // reach whatever sits behind the overlay even when the save in
        // flight means the dialog itself refuses to go.
        event.stopPropagation();
        if (busyRef.current) return;
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const nodes = focusable();
      if (nodes.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (!panel.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown, true);
    // The panel itself, not its first control: a dialog should announce
    // its title before its actions, and Tab from here still lands on the
    // first focusable inside.
    if (panelRef.current) panelRef.current.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (event) => {
    if (busy || !closeOnOverlay) return;
    if (event.target !== event.currentTarget) return;
    onClose?.();
  };

  const panelStyle =
    width === undefined
      ? undefined
      : { width: typeof width === 'number' ? `${width}px` : width };

  const panelClass = [
    'hms-modal',
    padded === false ? 'hms-modal--unpadded' : '',
    className,
  ].filter(Boolean).join(' ');

  const titleNode = title
    ? <h2 className="hms-modal-title" id={titleId}>{title}</h2>
    : null;

  let headerContent;
  if (typeof header === 'function') {
    headerContent = header(titleNode);
  } else if (header) {
    headerContent = <>{titleNode}{header}</>;
  } else {
    headerContent = titleNode;
  }

  return ReactDOM.createPortal(
    <div className="hms-modal-overlay" onClick={handleOverlayClick}>
      <div
        ref={panelRef}
        className={panelClass}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {(headerContent || showClose) && (
          <div className="hms-modal-header">
            <div className="hms-modal-header-content">{headerContent}</div>
            {showClose && (
              <button
                type="button"
                className="hms-modal-close"
                onClick={() => onClose?.()}
                disabled={busy}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        )}
        <div className="hms-modal-body">{children}</div>
        {footer && <div className="hms-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default HMSModal;
